import { useCallback, useRef, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { chatService } from '@/services/chatService';
import { mapChatMessagesToDisplaySegments } from '@/utils/chatMessages';
import { TranscriptContent } from '@/components/amazon-connect/TranscriptContent';
import { mapLiveChunksToSegments } from '@/components/amazon-connect/transcriptUtils';
import { useAmazonConnectContacts } from '@/hooks/useAmazonConnectContacts';
import { useChatConnectTranscript } from '@/hooks/useChatConnectTranscript';
import {
  getInitialOrCaptureContactId,
  isChatContact,
} from '@/components/amazon-connect/contactUtils';
import {
  Paper,
  Typography,
  TextField,
  Box,
  Button,
  CircularProgress,
} from '@gapartners/ui';
import type { CallMessage } from '@/types/call';

const EMPTY_MESSAGES: CallMessage[] = [];

interface LiveTranscriptCardProps {
  quickScriptDraft?: { sequence: number; content: string } | null;
}

export function LiveTranscriptCard({
  quickScriptDraft,
}: LiveTranscriptCardProps = {}) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const {
    contactId,
    isStreamInitializing,
    previousMessages,
    transcriptChunks,
    segmentNotes,
    callerName,
    customerOnHold,
  } = useAppSelector(state => state.call);
  const { user } = useAppSelector(state => state.auth);
  const { activeContacts } = useAmazonConnectContacts();
  const hasActiveCall = Boolean(contactId);
  const scrollRef = useRef<HTMLDivElement>(null);

  const activeContact = useMemo(
    () =>
      contactId
        ? (activeContacts.find(c => {
            try {
              return (
                getInitialOrCaptureContactId(c) === contactId ||
                c.getContactId() === contactId
              );
            } catch {
              // Contact may already be destroyed remotely (StateError).
              return false;
            }
          }) ?? null)
        : null,
    [activeContacts, contactId]
  );
  const isChat = activeContact != null && isChatContact(activeContact);

  // When the customer is on hold (manual or warm transfer) suppress incoming
  // customer transcript chunks — audio is paused so the transcription isn't
  // useful to the agent.  Redux still accumulates all chunks for dedup.
  // `customerOnHold` is maintained by ActiveCallBanner (1 s poll) and
  // useWarmTransfer (hold / resume callbacks).
  const isOnHold = !isChat && customerOnHold;

  // Snapshot of transcriptChunks.length at the moment hold was engaged.
  const [holdStartChunkIdx, setHoldStartChunkIdx] = useState<number | null>(
    null
  );

  useEffect(() => {
    if (isOnHold && holdStartChunkIdx === null) {
      setHoldStartChunkIdx(transcriptChunks.length);
    } else if (!isOnHold && holdStartChunkIdx !== null) {
      setHoldStartChunkIdx(null);
    }
  }, [isOnHold, transcriptChunks.length, holdStartChunkIdx]);

  // While on hold, hide incoming customer chunks (arrived during hold).
  // All chunks stay in Redux for dedup — only what reaches TranscriptContent changes.
  const displayChunks = useMemo(() => {
    if (holdStartChunkIdx === null) return transcriptChunks;
    return transcriptChunks.filter((chunk, idx) => {
      if (idx < holdStartChunkIdx) return true;
      return chunk.speaker !== 'customer';
    });
  }, [transcriptChunks, holdStartChunkIdx]);

  const messageCount = previousMessages.length + displayChunks.length;

  const controllerRef = useChatConnectTranscript(
    isChat,
    activeContact,
    dispatch
  );

  const [internalChatInput, setInternalChatInput] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);

  useEffect(() => {
    if (!quickScriptDraft) return;
    setInternalChatInput(quickScriptDraft.content);
    setSendError(null);
  }, [quickScriptDraft]);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, [messageCount]);

  const liveSegments = useMemo(
    () => mapLiveChunksToSegments(displayChunks, segmentNotes),
    [displayChunks, segmentNotes]
  );
  const chatSegments = useMemo(
    () =>
      isChat && !isStreamInitializing
        ? mapChatMessagesToDisplaySegments(previousMessages, segmentNotes, {
            clientName: callerName,
            agentName: user?.email,
          })
        : [],
    [
      callerName,
      isChat,
      isStreamInitializing,
      previousMessages,
      segmentNotes,
      user?.email,
    ]
  );
  const hasNoTranscriptContent =
    (isChat ? chatSegments.length : liveSegments.length) === 0 &&
    (!isChat ? previousMessages.length === 0 : true);
  const shouldShowTranscriptLoader =
    hasActiveCall && isStreamInitializing && (isChat || hasNoTranscriptContent);

  const handleRefreshChatMessageIds = useCallback(
    () =>
      contactId ? chatService.refreshChatMessageMetadata(contactId) : undefined,
    [contactId]
  );

  // When transcriptChunks (from bufferedTranscripts) are present, they already
  // cover the same conversation as previousMessages with proper speaker tags.
  // Only fall back to previousMessages when no transcript chunks exist yet.
  const transcriptPreviousMessages = isChat
    ? EMPTY_MESSAGES
    : displayChunks.length > 0
      ? EMPTY_MESSAGES
      : previousMessages;
  const scrollBoxSx = useMemo(
    () =>
      isChat
        ? {
            // Allow TranscriptContent to shrink so the input below remains visible.
            minHeight: 0,
            maxHeight: 'min(52vh, 38rem)',
            flex: 1,
          }
        : { maxHeight: '15rem', minHeight: '10rem' },
    [isChat]
  );

  const handleSendChat = async () => {
    const text = internalChatInput.trim();
    if (!text || sending || !controllerRef.current?.sendMessage) return;
    setSendError(null);
    setSending(true);
    try {
      await Promise.resolve(
        controllerRef.current.sendMessage({
          contentType: 'text/plain',
          message: text,
        })
      );
      setInternalChatInput('');
    } catch (err) {
      setSendError(
        err instanceof Error
          ? err.message
          : t('components.contactCenter.chatSendFailed', 'Failed to send')
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        minWidth: '16rem',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        ...(isChat
          ? {
              minHeight: '28rem',
              maxHeight: 'min(72vh, 44rem)',
              flex: 1,
            }
          : { minHeight: '12rem' }),
      }}
    >
      <Typography
        variant="subtitle2"
        fontWeight={600}
        sx={{ mb: 2, flexShrink: 0, wordBreak: 'break-word' }}
      >
        {t('components.contactCenter.liveTranscript')}
      </Typography>
      {!hasActiveCall ? (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ wordBreak: 'break-word' }}
        >
          {t('components.contactCenter.transcriptPlaceholder')}
        </Typography>
      ) : (
        <>
          {shouldShowTranscriptLoader ? (
            <Box
              sx={{
                minHeight: '10rem',
                maxHeight: '15rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CircularProgress size={24} />
            </Box>
          ) : (
            <TranscriptContent
              previousMessages={transcriptPreviousMessages}
              segments={isChat ? chatSegments : liveSegments}
              contactId={contactId ?? ''}
              showLiveTranscriptLabel={!isChat && previousMessages.length > 0}
              scrollContainerRef={scrollRef}
              scrollBoxSx={scrollBoxSx}
              size="compact"
              onChatMessageIdsRefresh={
                isChat ? handleRefreshChatMessageIds : undefined
              }
            />
          )}
          {isChat && (
            <Box
              sx={{
                mt: 2,
                pt: 2,
                borderTop: 1,
                borderColor: 'divider',
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                flexShrink: 0,
              }}
            >
              {sendError && (
                <Typography variant="caption" color="error">
                  {sendError}
                </Typography>
              )}
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <TextField
                  fullWidth
                  size="small"
                  variant="outlined"
                  placeholder={t(
                    'components.contactCenter.chatPlaceholder',
                    'Type a message…'
                  )}
                  value={internalChatInput}
                  onChange={e => {
                    setInternalChatInput(e.target.value);
                    if (sendError) setSendError(null);
                  }}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendChat();
                    }
                  }}
                  disabled={sending}
                  multiline
                  minRows={2}
                  maxRows={4}
                />
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSendChat}
                  disabled={!internalChatInput.trim() || sending}
                  sx={{ flexShrink: 0, minWidth: 96, alignSelf: 'center' }}
                >
                  {sending ? (
                    <CircularProgress size={22} color="inherit" />
                  ) : (
                    t('components.contactCenter.sendMessage', 'Send')
                  )}
                </Button>
              </Box>
            </Box>
          )}
        </>
      )}
    </Paper>
  );
}
